import { create } from 'zustand';

import { shareService } from '@/services/share-service';
import type { PropertyShare, CreateShareOptions } from '@/types/share';

interface ShareStore {
  /** Shares keyed by propertyId */
  sharesByProperty: Record<string, PropertyShare[]>;
  isLoading: boolean;
  error: string | null;

  loadShares: (propertyId: string) => Promise<void>;
  createShare: (propertyId: string, options?: CreateShareOptions) => Promise<PropertyShare>;
  revokeShare: (shareId: string, propertyId: string) => Promise<void>;
  deleteShare: (shareId: string, propertyId: string) => Promise<void>;
  reset: () => void;
}

export const useShareStore = create<ShareStore>((set) => ({
  sharesByProperty: {},
  isLoading: false,
  error: null,

  loadShares: async (propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const shares = await shareService.getSharesForProperty(propertyId);
      set((state) => ({
        sharesByProperty: { ...state.sharesByProperty, [propertyId]: shares },
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load shares';
      console.error('ShareStore.loadShares:', err);
      set({ error: message, isLoading: false });
    }
  },

  createShare: async (propertyId, options) => {
    try {
      const share = await shareService.createShare(propertyId, options);
      set((state) => {
        const existing = state.sharesByProperty[propertyId] ?? [];
        return {
          sharesByProperty: {
            ...state.sharesByProperty,
            [propertyId]: [share, ...existing],
          },
        };
      });
      return share;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create share';
      console.error('ShareStore.createShare:', err);
      set({ error: message });
      throw err;
    }
  },

  revokeShare: async (shareId, propertyId) => {
    try {
      await shareService.revokeShare(shareId);
      set((state) => {
        const existing = state.sharesByProperty[propertyId] ?? [];
        return {
          sharesByProperty: {
            ...state.sharesByProperty,
            [propertyId]: existing.filter((s) => s.id !== shareId),
          },
        };
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke share';
      console.error('ShareStore.revokeShare:', err);
      set({ error: message });
    }
  },

  deleteShare: async (shareId, propertyId) => {
    try {
      await shareService.deleteShare(shareId);
      set((state) => {
        const existing = state.sharesByProperty[propertyId] ?? [];
        return {
          sharesByProperty: {
            ...state.sharesByProperty,
            [propertyId]: existing.filter((s) => s.id !== shareId),
          },
        };
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete share';
      console.error('ShareStore.deleteShare:', err);
      set({ error: message });
    }
  },

  reset: () => set({ sharesByProperty: {}, isLoading: false, error: null }),
}));

/** Selector: shares for a given property, or empty array */
export function selectSharesForProperty(
  sharesByProperty: Record<string, PropertyShare[]>,
  propertyId: string,
): PropertyShare[] {
  return sharesByProperty[propertyId] ?? [];
}

/** Returns the full share URL for a token */
export function getShareUrl(token: string): string {
  return shareService.getShareUrl(token);
}
